import { Pagination } from '@shared/types';
import { Box, SxProps } from '@mui/material';
import { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';
import TablePagination from '@mui/material/TablePagination';

interface UtmsPaginationProps {
    pagination: Pagination;
    count: number;
    handleChangePagination: (newPagination: Pagination) => void;
    sx?: SxProps;
    view?: 'table' | 'cards';
}

const UtmsPagination: FC<UtmsPaginationProps> = ({ pagination, count, handleChangePagination, sx, view }) => {
    const { t } = useTranslation();

    const handlePageIndexChange = (_event: any, newPageIndex: number) => {
        handleChangePagination({ ...pagination, pageIndex: newPageIndex });
    };

    const handlePageSizeChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        handleChangePagination({ pageIndex: 0, pageSize: parseInt(event.target.value, 10) });
    };

    return (
        <TablePagination
            sx={{
                '& .MuiToolbar-root': {
                    paddingLeft: '0 !important',
                    minHeight: '0 !important',
                    flexDirection: 'row-reverse',
                    height: '100%',
                },
                '& .MuiToolbar-root > *:not(:nth-child(3)):not(:last-child):not(:nth-child(2))': {
                    padding: '0 8px',
                    borderRight: '1px solid var(--border-color)',
                },
                '& .MuiTablePagination-actions': {
                    marginLeft: '0 !important',
                },
                '& .MuiTablePagination-input': {
                    marginLeft: 0,
                    order: -1,
                    '& .MuiSelect-select': {
                        pl: 0,
                        pb: 0.5,
                        borderBottom: '1px solid var(--border-color)',
                    },
                },
                '& .MuiTablePagination-spacer': {
                    order: -2,
                },
                '& .MuiTablePagination-selectLabel': {
                    padding: '0 8px',
                },
                overflow: 'hidden',
                ...sx,
            }}
            component="div"
            count={count}
            page={pagination.pageIndex}
            showFirstButton
            showLastButton
            onPageChange={handlePageIndexChange}
            rowsPerPage={pagination.pageSize}
            onRowsPerPageChange={handlePageSizeChange}
            labelRowsPerPage={view === 'cards' ? t('cards_per_page') : t('records_per_page')}
            labelDisplayedRows={({ from, to, count }) => (
                <Box component="span" sx={{ display: 'flex', gap: 1 }}>
                    {`${t('shown')}:`}
                    <Box component="span" sx={{ display: 'flex', gap: 0.5 }}>
                        <Box component="span" sx={{ fontWeight: 700 }}>
                            {from === 1 && to === count ? t('all') : `${from}-${to}`}
                        </Box>
                        <Box component="span">{`${t('of')} ${count}`}</Box>
                    </Box>
                </Box>
            )}
            slotProps={{
                actions: {
                    lastButton: {
                        sx: {
                            position: 'relative',
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                width: '0.75px',
                                height: '1.1rem',
                                top: '50%',
                                right: 0,
                                background: 'var(--border-color)',
                                transform: 'translateY(-50%)',
                            },
                        },
                    },
                    firstButtonIcon: {
                        fontSize: 'small',
                    },
                },
            }}
        />
    );
};

export default UtmsPagination;
